import React from 'react';
import { FmButton } from '../FmButton';
import { FmCheckbox } from '../FmCheckbox';
import { FmSelect } from '../FmSelect';
import { FmSelector } from '../FmSelector';
import { FmTable, type FmTableColumn } from '../FmTable';
import { plainHeadingText, useInheritedHostedHeading } from '../fmHostedHeading';
import { renderFmTableCell } from '../FmTableCell';
import type {
  FmStoryRankedTableRow,
  FmStoryTableControl,
  FmStoryTableRow,
} from './elementFields.types';
import { FmComparisonView } from './FmComparisonElement';
import { buildTableColumns } from './elementAdapters';
import { useElementActionInhibition } from './elementInhibition';
import { derivedTestId, useElementContext } from './elementContext';
import type { FmTableDisclosure, FmTableElement, FmTablePreview } from './elements.types';
import './FmElements.css';

/** One table control: a segmented/radio selector or a toggle, above the rows it filters. */
export const FmTableControlView: React.FC<{ control: FmStoryTableControl }> = ({ control }) => {
  if (control.kind === 'toggle') {
    return (
      <FmCheckbox
        checked={control.checked}
        onChange={(checked) => control.onChange?.(checked)}
        text={control.label}
        data-testid={control.testId}
      />
    );
  }
  return (
    <div className="fm-element-table__control">
      <span>{control.label}</span>
      <FmSelector
        variant={control.variant ?? 'segmented'}
        value={control.value}
        onChange={(value) => control.onChange?.(value)}
        label={control.label}
        items={control.options.map((option) => ({
          value: option.value,
          label: option.label,
          disabled: option.disabled,
          'data-testid': option.testId,
        }))}
        data-testid={control.testId}
      />
    </div>
  );
};

/**
 * Top-N preview with one reveal toggle; every row stays reachable. A
 * route-return target beyond the preview opens it, so focus restoration finds
 * a mounted row.
 */
function usePreview<Item>(
  rows: readonly Item[],
  preview: FmTablePreview | undefined,
  keyOf?: (row: Item) => string,
) {
  const limit = preview?.limit ?? rows.length;
  const revealKey = preview?.revealRowKey;
  const revealIndex = revealKey && keyOf ? rows.findIndex((row) => keyOf(row) === revealKey) : -1;
  const revealNeedsAll = revealIndex >= limit;
  const [expanded, setExpanded] = React.useState(revealNeedsAll);
  React.useLayoutEffect(() => {
    if (revealNeedsAll) setExpanded(true);
  }, [revealNeedsAll, revealKey]);
  const hasOverflow = rows.length > limit;
  const visible = expanded || !hasOverflow ? [...rows] : rows.slice(0, limit);
  return { visible, hasOverflow, expanded, toggle: () => setExpanded((prev) => !prev) };
}

const RevealButton: React.FC<{
  preview: FmTablePreview;
  expanded: boolean;
  onToggle: () => void;
  testId: string;
}> = ({ preview, expanded, onToggle, testId }) => (
  <div className="fm-element-table__reveal">
    <FmButton
      variant="bordered"
      size="sm"
      onClick={onToggle}
      aria-expanded={expanded}
      data-testid={testId}
    >
      {expanded ? preview.showLessLabel : preview.showAllLabel}
    </FmButton>
  </div>
);

/**
 * Select columns render a row's own select cell; a row without one prints its
 * field as text. The filter id is stamped on the control and each option so a
 * route can find the select it restores focus to.
 */
function withSelectColumns(
  columns: FmTableColumn<FmStoryTableRow>[],
  disclosure: FmTableDisclosure | undefined,
): FmTableColumn<FmStoryTableRow>[] {
  const selects = disclosure?.selectColumns;
  if (!selects) return columns;
  return columns.map((column) => {
    const select = selects.find((entry) => entry.columnKey === column.key);
    if (!select) return column;
    const renderText = column.render;
    return {
      ...column,
      flushInline: true,
      render: (row: FmStoryTableRow) => {
        const cell = select.cells[row.key];
        if (!cell) return renderText ? renderText(row) : String(row[column.key] ?? '');
        const value = String(row[column.key] ?? '');
        return (
          <FmSelect<string>
            value={value}
            options={cell.options.map((option) => ({ ...option }))}
            optionRender={(option) => (
              <span
                data-select-option={cell.filterId}
                data-selected={String(option.value === value)}
                data-value={String(option.value)}
              >
                {option.label}
              </span>
            )}
            onChange={(next) => select.onChange(row.key, String(next))}
            ariaLabel={cell.ariaLabel}
            disabled={cell.disabled}
            controlWidth="fill"
            data-testid={`select-${cell.filterId}`}
          />
        );
      },
    };
  });
}

function expandableFor(
  disclosure: FmTableDisclosure | undefined,
  cellOptions: { emptyLabel: string; locale?: string },
) {
  const details = disclosure?.expandableRows;
  if (!details) return undefined;
  return {
    expandMode: 'single' as const,
    rowExpandable: (row: FmStoryTableRow) => Boolean(details[row.key]),
    expandedRowRender: (row: FmStoryTableRow) => (
      <dl className="fm-element-table__details">
        {(details[row.key] ?? []).map((detail) => (
          <div key={detail.id}>
            <dt>{detail.label}</dt>
            <dd>
              {renderFmTableCell(detail.value, cellOptions)}
              {detail.detail ? <small>{detail.detail}</small> : null}
            </dd>
          </div>
        ))}
      </dl>
    ),
  };
}

/**
 * A caption that repeats the heading the section printed stays the figure's
 * accessible name only: one section, one title.
 */
function useCaptionRepeatsHeading(caption: string): boolean {
  const host = useInheritedHostedHeading();
  return host !== null && host.title !== null && plainHeadingText(caption) === host.title;
}

const DataTable: React.FC<{
  table: Extract<FmTableElement, { table: unknown }>['table'];
  disclosure?: FmTableDisclosure;
  testBase: string;
}> = ({ table, disclosure, testBase }) => {
  const inhibitionReason = useElementActionInhibition();
  const { visible, hasOverflow, expanded, toggle } = usePreview(
    table.rows,
    disclosure?.preview,
    (row) => row.key,
  );
  const groupByField = disclosure?.groupByField;
  const cellOptions = { emptyLabel: table.emptyCellLabel ?? '', locale: table.locale };
  const captionRepeatsHeading = useCaptionRepeatsHeading(table.caption);
  return (
    <figure className="fm-element-table" aria-label={table.caption}>
      {table.controls?.length ? (
        <div className="fm-element-table__controls">
          {table.controls.map((control) => (
            <FmTableControlView key={control.id} control={control} />
          ))}
        </div>
      ) : null}
      <div className="fm-element-table__frame" data-testid={`${testBase}-wrap`}>
        <FmTable<FmStoryTableRow>
          columns={withSelectColumns(buildTableColumns(table, inhibitionReason), disclosure)}
          rows={visible}
          rowKey={(row) => row.key}
          // Dense operational rows sit in the region's own band and keep the
          // band surface behind their header.
          stickyHead={table.layout !== 'operational'}
          density="compact"
          layout={table.layout}
          mobileLayout={table.mobileLayout ?? 'labelled-cells'}
          emptyCellLabel={table.emptyCellLabel}
          locale={table.locale}
          groupBy={groupByField ? (row) => String(row[groupByField] ?? '') : undefined}
          groupCountLabel={groupByField ? () => '' : undefined}
          expandable={expandableFor(disclosure, cellOptions)}
          emptyText={table.emptyText}
          rowTestId={table.rowTestId}
          rowAriaLabel={table.rowAriaLabel}
          rowCurrent={(row) => row.key === table.selectedRowId}
          rowDataAttributes={(row) => ({ 'data-row-key': row.key })}
          onRowSelect={table.onRowSelect ? (row) => table.onRowSelect?.(row.key) : undefined}
          data-testid={table.tableTestId ?? testBase}
        />
      </div>
      {hasOverflow && disclosure?.preview ? (
        <RevealButton
          preview={disclosure.preview}
          expanded={expanded}
          onToggle={toggle}
          testId={`${testBase}-reveal`}
        />
      ) : null}
      {captionRepeatsHeading ? null : <figcaption>{table.caption}</figcaption>}
    </figure>
  );
};

type RankedTable = Extract<FmTableElement, { rankedTable: unknown }>['rankedTable'];

/**
 * The ranked selection the owner sees. A controlled caller owns it outright,
 * including "nothing selected"; only an uncontrolled table keeps local state.
 */
function useRankedSelection(ranking: RankedTable) {
  const [localSelectedId, setLocalSelectedId] = React.useState(ranking.rows[0]?.id ?? '');
  const [localLensId, setLocalLensId] = React.useState(
    ranking.lenses?.find((lens) => !lens.disabled)?.id ?? '',
  );
  const selectedRowId = ranking.selectionControlled
    ? (ranking.selectedRowId ?? null)
    : (ranking.selectedRowId ?? localSelectedId);
  const selectRow = (row: FmStoryRankedTableRow): void => {
    if (!ranking.selectionControlled && ranking.selectedRowId === undefined) {
      setLocalSelectedId(row.id);
    }
    ranking.onRowSelect?.(row.id);
  };
  const changeLens = (lensId: string): void => {
    if (ranking.activeLensId === undefined) setLocalLensId(lensId);
    ranking.onLensChange?.(lensId);
  };
  return { selectedRowId, selectRow, lensId: ranking.activeLensId ?? localLensId, changeLens };
}

function rankedColumns(ranking: RankedTable): FmTableColumn<FmStoryRankedTableRow>[] {
  return ranking.columns.map((column) => ({
    key: column.key,
    header: column.header,
    label: column.header,
    role: column.role,
    numeric: column.numeric,
    render: (row) => String(row.values[column.key] ?? ''),
  }));
}

const RankedTableView: React.FC<{
  ranking: RankedTable;
  preview?: FmTablePreview;
  testBase: string;
}> = ({ ranking, preview, testBase }) => {
  const selection = useRankedSelection(ranking);
  const { visible, hasOverflow, expanded, toggle } = usePreview(ranking.rows, preview);
  const base = ranking.testId ?? testBase;
  if (ranking.unavailableReason) {
    return <div className="fm-element-unavailable">{ranking.unavailableReason}</div>;
  }
  return (
    <div className="fm-element-ranked-table" data-testid={ranking.testId}>
      <div className="fm-element-ranked-table__controls">
        <strong>{ranking.objective}</strong>
        {ranking.lenses?.length ? (
          <FmSelector
            variant="segmented"
            value={selection.lensId}
            label={ranking.lensLabel ?? ranking.objective}
            items={ranking.lenses.map((lens) => ({
              value: lens.id,
              label: lens.label,
              disabled: lens.disabled,
              'data-testid': `${base}-${lens.id}-lens`,
            }))}
            onChange={selection.changeLens}
            data-testid={`${base}-lens-selector`}
          />
        ) : null}
      </div>
      <FmTable<FmStoryRankedTableRow>
        columns={rankedColumns(ranking)}
        rows={visible}
        rowKey={(row) => row.id}
        caption={ranking.tableCaption}
        density="compact"
        mobileLayout="labelled-cells"
        nonInteractiveHead
        onRowSelect={ranking.onRowSelect ? selection.selectRow : undefined}
        rowAriaLabel={(row) => row.ariaLabel}
        rowCurrent={(row) => row.id === selection.selectedRowId}
        rowDataAttributes={(row) => ({ 'data-selected': row.id === selection.selectedRowId })}
        rowTestId={(row) => row.testId}
        emptyText={ranking.emptyLabel}
        data-testid={`${base}-table`}
      />
      {hasOverflow && preview ? (
        <RevealButton
          preview={preview}
          expanded={expanded}
          onToggle={toggle}
          testId={`${base}-reveal`}
        />
      ) : null}
      <ul
        className="fm-element-ranked-table__insights"
        aria-label={ranking.insightsLabel ?? ranking.objective}
      >
        {ranking.insights.map((insight) => (
          <li key={insight.id} data-tone={insight.tone ?? 'neutral'}>
            <strong>{insight.title}</strong>
            <p>{insight.body}</p>
          </li>
        ))}
      </ul>
      {ranking.assumptions ? (
        <p className="fm-element-ranked-table__assumptions">{ranking.assumptions}</p>
      ) : null}
    </div>
  );
};

/**
 * The table element: a data table, a ranked table or a comparison matrix, each
 * with the one disclosure option set; or the table's own unavailable reason.
 */
export const FmTableElementView: React.FC<{ element: FmTableElement }> = ({ element }) => {
  const context = useElementContext();
  if (element.table) {
    return (
      <DataTable
        table={element.table}
        disclosure={element.disclosure}
        testBase={derivedTestId(context, element.testId, 'table')}
      />
    );
  }
  if (element.rankedTable) {
    return (
      <RankedTableView
        ranking={element.rankedTable}
        preview={element.disclosure?.preview}
        testBase={derivedTestId(context, element.testId, 'ranked-table')}
      />
    );
  }
  if (element.comparison) {
    return (
      <FmComparisonView
        comparison={element.comparison}
        testBase={derivedTestId(context, element.testId, 'comparison')}
      />
    );
  }
  return (
    <div className="fm-element-unavailable" data-state="empty" data-testid={element.testId}>
      {element.unavailableReason}
    </div>
  );
};
